import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Star,
  Instagram,
  Youtube,
  MapPin,
  Globe,
  ArrowLeft,
  TrendingUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { fetchCreator, updateCreator } from "@/lib/api";
import { formatFollowers, formatINR } from "@/lib/format";

const PlatformIcon = ({ name }) =>
  name === "YouTube" ? <Youtube className="h-4 w-4" /> : <Instagram className="h-4 w-4" />;

const StatusBadge = ({ status }) => {
  const m = {
    Approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    Submitted: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    Pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    Completed: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
    "In Review": "bg-purple-500/10 text-purple-300 border-purple-500/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${m[status] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"}`}>
      {status}
    </span>
  );
};

const CreatorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState(4);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCreator(id)
      .then((c) => {
        setCreator(c);
        setNotes(c.notes || "");
        setRating(c.rating || 4);
      })
      .catch(() => setCreator(false));
  }, [id]);

  if (creator === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (creator === false) {
    return (
      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-12 text-center">
        <p className="text-zinc-400">Creator not found.</p>
        <Button className="mt-4" onClick={() => navigate(-1)}>Go back</Button>
      </div>
    );
  }

  const saveNotes = async () => {
    setSaving(true);
    try {
      await updateCreator(creator.id, { notes, rating });
      toast.success("Saved");
    } catch (_e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const setNewRating = async (n) => {
    setRating(n);
    try {
      await updateCreator(creator.id, { rating: n });
      toast.success(`Internal rating: ${n}`);
    } catch (_e) {
      toast.error("Could not update rating");
    }
  };

  const maxAudience = Math.max(...(creator.audience?.ageGroups || [{ pct: 1 }]).map((a) => a.pct));

  return (
    <div className="space-y-7" data-testid="creator-profile-page">
      <Link to="/app/creators" className="text-sm text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1.5">
        <ArrowLeft className="h-4 w-4" /> All creators
      </Link>

      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-blue-500/[0.05] to-transparent p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <img src={creator.avatar} alt={creator.name} className="h-24 w-24 rounded-2xl object-cover ring-1 ring-white/10" />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight">{creator.name}</h1>
              <span className="text-zinc-500 text-sm">{creator.handle}</span>
              <span className="inline-flex items-center gap-1 text-amber-300 text-sm">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {rating}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-zinc-400">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {creator.location}</span>
              <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {(creator.languages || []).join(", ")}</span>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {(creator.platforms || []).map((p) => (
                <div key={p.name} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/8 text-sm">
                  <PlatformIcon name={p.name} /> {formatFollowers(p.followers)}
                  <span className="text-zinc-500 text-xs">on {p.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(creator.niches || []).map((n) => (
                <span key={n} className="text-xs px-2 py-0.5 rounded bg-white/[0.04] border border-white/8 text-zinc-300">{n}</span>
              ))}
            </div>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="assign-campaign-btn" onClick={() => toast.success("Creator queued for campaign assignment")}>
            Assign to Campaign
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { k: "Engagement Rate", v: `${creator.er}%`, t: "+0.4 vs niche" },
          { k: "Avg Views", v: formatFollowers(creator.avgViews || 0), t: "Last 30 posts" },
          { k: "Followers", v: formatFollowers(creator.followers), t: creator.primaryPlatform },
          { k: "Reel Rate", v: formatINR(creator.rateCard?.reel || 0), t: "Net of GST" },
        ].map((s) => (
          <div key={s.k} className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500">{s.k}</div>
            <div className="mt-2 text-2xl font-semibold tracking-tighter text-zinc-50">{s.v}</div>
            <div className="text-[11px] text-emerald-400 mt-1 inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {s.t}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Audience Demographics</h2>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Gender</div>
              <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden flex">
                <div className="bg-pink-500" style={{ width: `${creator.audience?.female || 0}%` }} />
                <div className="bg-blue-500" style={{ width: `${creator.audience?.male || 0}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-zinc-400">
                <span>♀ {creator.audience?.female || 0}%</span>
                <span>♂ {creator.audience?.male || 0}%</span>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Age</div>
              <div className="space-y-2">
                {(creator.audience?.ageGroups || []).map((a) => (
                  <div key={a.age} className="flex items-center gap-3">
                    <span className="w-12 text-xs text-zinc-400">{a.age}</span>
                    <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${(a.pct / maxAudience) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right text-xs text-zinc-300">{a.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Rate Card</h2>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-zinc-400">Reel</span><span className="text-zinc-100 font-medium">{formatINR(creator.rateCard?.reel || 0)}</span></div>
            <div className="flex items-center justify-between"><span className="text-zinc-400">Post</span><span className="text-zinc-100 font-medium">{formatINR(creator.rateCard?.post || 0)}</span></div>
            <div className="flex items-center justify-between"><span className="text-zinc-400">Story</span><span className="text-zinc-100 font-medium">{formatINR(creator.rateCard?.story || 0)}</span></div>
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base font-semibold tracking-tight">Internal Notes</h2>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setNewRating(n)}
                aria-label={`Set rating ${n}`}
                data-testid={`rating-star-${n}`}
              >
                <Star className={`h-4 w-4 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
              </button>
            ))}
          </div>
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-3 bg-white/[0.03] border-white/10 text-zinc-100"
          data-testid="creator-notes-textarea"
        />
        <div className="mt-3">
          <Button onClick={saveNotes} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-notes-btn">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving</> : "Save Notes"}
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Campaign History</h2>
        {(creator.history || []).length === 0 ? (
          <p className="mt-3 text-sm text-zinc-500">No campaigns yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                <tr className="text-left">
                  <th className="py-2 font-medium">Campaign</th>
                  <th className="py-2 font-medium">Brand</th>
                  <th className="py-2 font-medium">Date</th>
                  <th className="py-2 font-medium">Deliverables</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {creator.history.map((h, i) => (
                  <tr key={i}>
                    <td className="py-3 text-zinc-100">{h.campaign}</td>
                    <td className="py-3 text-zinc-300">{h.brand}</td>
                    <td className="py-3 text-zinc-400">{h.date}</td>
                    <td className="py-3 text-zinc-300">{h.deliverables}</td>
                    <td className="py-3"><StatusBadge status={h.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default CreatorProfile;
